import {
  Component,
  HostBinding,
  OnInit,
  computed,
  input,
  output,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { GlobalStore, UserService } from '@maximizer/core/shared/data-access';
import { ListItem } from '@maximizer/core/shared/domain';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { StringValidator } from '@maximizer/core/shared/ui/validators';
import { NoteService } from '@maximizer/outlook/note/data-access';
import {
  AbEntryService,
  NavigationService,
  OutlookStore,
} from '@maximizer/outlook/shared/data-access';
import {
  AbEntryLastContactDatePreference,
  EmailEntryType,
  EntryType,
  NoteForm,
  NoteModuleConfiguration,
  NotePrefill,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DATEINPUTS } from '@progress/kendo-angular-dateinputs';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_TOOLTIPS } from '@progress/kendo-angular-tooltip';
import { catchError, finalize, forkJoin, of } from 'rxjs';

@Component({
  selector: 'maximizer-add-note',
  templateUrl: './add-note.component.html',
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_DATEINPUTS,
    KENDO_DROPDOWNS,
    KENDO_INPUTS,
    KENDO_LABEL,
    KENDO_TOOLTIPS,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class AddNoteComponent implements OnInit {
  @HostBinding('class.max-outlook') hostClass = true;

  notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  type = input.required<EmailEntryType>();
  id = input.required<string>();
  embedded = input(false);
  prefill = input<NotePrefill | null>(null);

  saved = output<void>();
  cancelled = output<void>();

  leadKey = computed(() => {
    return this.type() === EntryType.Lead ? decodeURI(this.id()) : null;
  });

  abEntryKey = computed(() => {
    return this.type() === EntryType.AbEntry ? decodeURI(this.id()) : null;
  });

  currentId = computed(() => {
    return this.leadKey() ?? this.abEntryKey() ?? '';
  });

  EntryType = EntryType;
  form?: FormGroupWrapper<NoteForm>;
  configuration?: NoteModuleConfiguration;
  loading = true;
  canSave = false;
  now = new Date();
  exitOnboarding = false;
  noneValue: ListItem<string>;
  parentKey?: string;
  abEntryType?: string;
  lastContactDate?: Date;

  constructor(
    private readonly noteService: NoteService,
    private readonly translate: TranslateService,
    private readonly userService: UserService,
    private readonly navigationService: NavigationService,
    private readonly abEntryService: AbEntryService,
    public outlookStore: OutlookStore,
    public globalStore: GlobalStore,
  ) {
    this.noneValue = {
      id: '-1',
      name: this.translate.instant('outlook.forms.none'),
    };
  }

  ngOnInit(): void {
    this.setForm();

    this.configuration = {
      options: {
        categories: [this.noneValue],
        types: [this.noneValue],
      },
      users: [],
    };

    this.form?.patch({
      category: this.noneValue.id,
    });

    this.applyPrefill();

    if (this.leadKey() || this.abEntryKey()) this.loadData();
  }

  private applyPrefill(): void {
    const prefill = this.prefill();
    if (!prefill || !this.form) return;

    this.form.patch({
      ...(prefill.richText === null ? {} : { richText: prefill.richText }),
      ...(prefill.dateTime === null || prefill.dateTime === undefined ? {} : { dateTime: prefill.dateTime }),
      ...(prefill.category === null ? {} : { category: prefill.category }),
      ...(prefill.important === null ? {} : { important: prefill.important }),
    });
  }

  private setForm(): void {
    this.form = FormWrapperBuilder.group<NoteForm>(
      {
        key: null,
        parentKey: this.abEntryKey() ?? this.leadKey(),
        dateTime: [this.now, [Validators.required]],
        richText: [
          null,
          {
            validators: Validators.compose([
              Validators.required,
              Validators.minLength(1),
              StringValidator.NotEmpty(),
            ]),
          },
        ],
        important: false,
        category: '-1',
        type: '-1',
        creator: [
          this.globalStore.session()?.user.id ?? null,
          {
            validators: Validators.compose([
              Validators.required,
              Validators.minLength(1),
              StringValidator.NotEmpty(),
            ]),
            updateOn: 'blur',
          },
        ],
        abEntryKey: null,
        leadKey: null,
      },

      this.save,
      () => {
        this.form?.control.setErrors(null);
        if (this.form?.valid) {
          this.canSave = true;
        } else {
          this.canSave = false;
        }
      },
    );
  }

  private loadData(): void {
    const request = {
      interactionOptions: this.noteService.getFieldOption(this.currentId()),
      users: this.userService.getActiveUsers(),
      abEntry:
        this.type() === EntryType.AbEntry
          ? this.abEntryService.getByKey(this.currentId())
          : of(null),
    };

    forkJoin(request)
      .pipe(
        catchError((error) => {
          console.error('Error loading data:', error);
          this.notification().show('load', 'error');
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe((data) => {
        if (!data) return;

        const { categories, types } = data.interactionOptions;

        categories.unshift(this.noneValue);
        types.unshift(this.noneValue);

        this.configuration = {
          options: {
            categories: categories ?? [this.noneValue],
            types: types ?? [this.noneValue],
          },
          users: data.users ?? [],
        };

        this.form?.patch({
          creator: this.globalStore.session()?.user.id,
          category: this.noneValue.id,
        });

        if (data.abEntry) {
          this.handleAbEntryProperties(data.abEntry);
        }
      });
  }

  private handleAbEntryProperties(abEntry: any): void {
    this.abEntryType = abEntry.type;
    this.lastContactDate = abEntry.lastContactDateValue
      ? new Date(abEntry.lastContactDateValue)
      : undefined;

    if (abEntry.type === EntryType.Contact && abEntry.parentKey) {
      this.parentKey = abEntry.parentKey;
    }
  }

  transformToNullWhenNone(input: string | null): string | null {
    if (input == this.noneValue.id) return null;
    return input;
  }

  save(): void {
    if (!this.form) {
      return;
    }
    const category = this.form.value.category;
    this.form.patch({
      category: this.transformToNullWhenNone(this.form.value.category),
      type: 0,
    });

    const validation = this.form.validate();
    if (!validation.valid) return;

    this.loading = true;
    this.noteService
      .create(this.form.value)
      .pipe(
        catchError(() => {
          this.loading = false;
          this.form?.patch({ category: category });
          this.notification().show('create', 'error');
          return of(null);
        }),
      )
      .subscribe((result) => {
        if (result === null) return;

        const isAbEntry = this.type() === EntryType.AbEntry;
        const formDate = this.form?.value.dateTime ?? new Date();

        if (isAbEntry) {
          this.updateLastContactDate(formDate);
        } else {
          this.completeSuccessNoteCreation();
        }
      });
  }

  cancel(): void {
    if (this.embedded()) {
      this.cancelled.emit();
      return;
    }
    this.navigationService.navigateBack();
  }

  private updateLastContactDate(date: Date): void {
    this.abEntryService
      .updateLastContactDateWithPreferences(
        this.currentId(),
        this.type(),
        AbEntryLastContactDatePreference.NoteCreated,
        date,
        this.abEntryType,
        this.parentKey,
      )
      .pipe(finalize(() => this.completeSuccessNoteCreation()))
      .subscribe();
  }

  private completeSuccessNoteCreation(): void {
    this.loading = false;
    if (this.embedded()) {
      this.saved.emit();
      return;
    }
    this.notification().show('create', 'success');
    this.navigationService.navigateBack();
  }
}
